import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Button, Spin } from 'antd';
import { openNotification } from 'util/helpers';
import { LoadingOutlined } from '@ant-design/icons';
import {
  useFindManyUserSceneAssetsQuery,
  useUpdateUserSceneAssetsMutation,
  FindManyUserSceneAssetsDocument,
} from '@src/components/generated/graphql';
import styles from './link-asset.module.css';

const notify = (type, msg, desc) => {
  openNotification(msg, desc, 'top', 2, type);
};

function getMetadata(item) {
  try {
    const metadata = JSON.parse(item?.metadata);
    return metadata;
  } catch (err) {
    return null;
  }
}

const LinkAsset = ({ id, isOpen, close }) => {
  const [updateAsset] = useUpdateUserSceneAssetsMutation();
  const [loading, setIsLoading] = useState(false);
  const [enablingLinking, setIsEnablingLinking] = useState(false);
  const [enabledLinking, setIsEnabledLinking] = useState(false);
  const [disabled, setDisabled] = useState(true);

  const { data: sceneAssets, loading: load } = useFindManyUserSceneAssetsQuery({
    variables: {
      where: {
        id: {
          equals: id,
        },
      },
    },
    fetchPolicy: 'no-cache',
  });
  const asset = sceneAssets?.findManyUserSceneAssets[0];

  const onFinish = async (values) => {
    setIsLoading(true);
    let metadata = getMetadata(asset);
    if (!metadata) {
      metadata = {};
    }
    metadata.link = values.link;
    await updateAsset({
      variables: {
        where: {
          id,
        },
        data: {
          metadata: {
            set: JSON.stringify(metadata),
          },
          enableLinking: {
            set: true,
          },
        },
      },
      refetchQueries: [FindManyUserSceneAssetsDocument],
    });
    setIsEnabledLinking(true);
    setIsLoading(false);
    notify('success', 'Successfully', 'saved changes');
    close();
  };

  const deleteLinking = async () => {
    setIsEnablingLinking(true);
    let metadata = getMetadata(asset);
    if (!metadata) {
      metadata = {};
    }
    metadata.link = '';
    await updateAsset({
      variables: {
        where: {
          id,
        },
        data: {
          enableLinking: {
            set: false,
          },
          metadata: {
            set: JSON.stringify(metadata),
          },
        },
      },
      refetchQueries: [FindManyUserSceneAssetsDocument],
    });
    setIsEnabledLinking(false);
    setIsEnablingLinking(false);
    notify('success', 'Successfully', 'Deleted link');
    close();
  };

  const onChange = (e) => {
    const value = e.target.value;
    const metadata = getMetadata(asset);
    if (value === metadata?.link) {
      setDisabled(true);
    } else {
      setDisabled(false);
    }
  };

  useEffect(() => {
    if (sceneAssets?.findManyUserSceneAssets) {
      setIsEnabledLinking(asset?.enableLinking as boolean);
    }
  }, [sceneAssets?.findManyUserSceneAssets, id]);

  if (load) {
    return <></>;
  }

  return (
    <Modal
      open={isOpen}
      onCancel={() => close()}
      footer={false}
      title='Link asset'
      destroyOnClose={true}
      afterClose={() => setDisabled(true)}
    >
      <Form onFinish={onFinish} layout='vertical'>
        <Form.Item
          name='link'
          label='Link URL'
          initialValue={getMetadata(asset)?.link ?? ''}
          rules={[{ required: true, message: 'Please enter link URL' }]}
        >
          <Input placeholder='URL' onChange={onChange} />
        </Form.Item>
        <div className={styles.btnWrapper}>
          {enabledLinking && (
            <Button
              type='dashed'
              onClick={deleteLinking}
              style={{ marginRight: 20 }}
            >
              {enablingLinking ? (
                <Spin indicator={<LoadingOutlined />} spinning />
              ) : (
                <span>Delete link</span>
              )}
            </Button>
          )}
          <Button
            htmlType='submit'
            type='primary'
            className={styles.btn}
            disabled={disabled}
          >
            {loading ? (
              <Spin
                indicator={
                  <LoadingOutlined
                    style={{ fontSize: 30, color: '#fff' }}
                    spin
                  />
                }
              ></Spin>
            ) : (
              'Save'
            )}
          </Button>
        </div>
      </Form>
    </Modal>
  );
};

export default LinkAsset;
